/**
 * The staff team-configuration registry: one typed list that the sub-nav
 * and the route table under /employees/teams/:teamId key on, the way
 * components/settings/sections.ts does for /settings. Adding a page is
 * one entry here plus one component file; App.tsx does not change.
 *
 * The whole area is staff-only (EmployeeRoute in App.tsx), and entering
 * it ends impersonation (TeamConfigLayout): every page edits the team
 * named in the URL through /api/internal as the staff member themselves.
 * So there is no access field: who may open the area decides everything.
 *
 * One sub-nav heading holds every page today. When a second is wanted,
 * add a `group` field the way SettingsGroup does in the Settings registry.
 *
 * Copy gate: `label` and `description` render in PageHeader, so the
 * CLAUDE.md rules apply (no em dashes, no trailing tails).
 */

import {
  AtSign,
  Boxes,
  Cable,
  Database,
  GitPullRequest,
  Layers,
  MoonStar,
  PanelLeft,
  Play,
  Receipt,
  Trash2,
  type LucideIcon,
} from 'lucide-react'

import type { ImpersonatableTeam } from '@/api/generated/model'

import { TeamBillingSection } from './TeamBillingSection'
import { TeamBucketsSection } from './TeamBucketsSection'
import { TeamConnectionsSection } from './TeamConnectionsSection'
import { TeamDeleteSection } from './TeamDeleteSection'
import { TeamEmailDomainsSection } from './TeamEmailDomainsSection'
import { TeamEvalPrsSection } from './TeamEvalPrsSection'
import { TeamEvalsConfigSection } from './TeamEvalsConfigSection'
import { TeamJobsSection } from './TeamJobsSection'
import { TeamModelsSection } from './TeamModelsSection'
import { TeamPipelinesSection } from './TeamPipelinesSection'
import { TeamSidebarSection } from './TeamSidebarSection'

export interface TeamConfigSection {
  /** URL segment under /employees/teams/:teamId: 'sidebar' renders at .../sidebar. */
  path: string
  label: string
  /** The PageHeader description. */
  description: string
  icon: LucideIcon
  Component: React.ComponentType<TeamConfigSectionProps>
}

/**
 * Every page receives its own registry entry, so the label and description
 * exist in exactly one place, and the team it configures.
 */
export interface TeamConfigSectionProps {
  section: TeamConfigSection
  team: ImpersonatableTeam
}

const SIDEBAR_SECTION: TeamConfigSection = {
  path: 'sidebar',
  label: 'Sidebar',
  description: 'Choose which pages this team sees in its sidebar and where it lands after sign-in.',
  icon: PanelLeft,
  Component: TeamSidebarSection,
}

export const TEAM_CONFIG_SECTIONS: readonly TeamConfigSection[] = [
  SIDEBAR_SECTION,
  {
    path: 'email-domains',
    label: 'Email domains',
    description: 'Set the email domains whose users join this team when they sign in.',
    icon: AtSign,
    Component: TeamEmailDomainsSection,
  },
  {
    path: 'models',
    label: 'Models',
    description:
      'Register the models this team can call through the gateway and see where each one is served.',
    icon: Boxes,
    Component: TeamModelsSection,
  },
  {
    path: 'connections',
    label: 'Connections',
    description:
      "Link this team's GitHub App installation and assign the S3 connections its buckets are read through. Benchmarks and batches pick from them.",
    icon: Cable,
    Component: TeamConnectionsSection,
  },
  {
    path: 'buckets',
    label: 'Buckets',
    description: "Set the buckets this team's data and traces live in.",
    icon: Database,
    Component: TeamBucketsSection,
  },
  {
    path: 'pipelines',
    label: 'Pipelines',
    description:
      'Grant the batch pipelines this team may submit to. A valid API key alone is not enough.',
    icon: Layers,
    Component: TeamPipelinesSection,
  },
  {
    path: 'evals-config',
    label: 'Evals config',
    description:
      "Set this team's benchmarks and pick the one the night scores.",
    icon: MoonStar,
    Component: TeamEvalsConfigSection,
  },
  {
    path: 'eval-prs',
    label: 'Eval PRs',
    description:
      'Set what eval PRs scrub and where in the repository they write generated tests.',
    icon: GitPullRequest,
    Component: TeamEvalPrsSection,
  },
  {
    path: 'jobs',
    label: 'Jobs',
    description: "Start one of this team's jobs outside its schedule.",
    icon: Play,
    Component: TeamJobsSection,
  },
  {
    path: 'billing',
    label: 'Billing',
    description:
      'See the gateway billing config for this team and its last 30 days of traffic. Rates are read-only here.',
    icon: Receipt,
    Component: TeamBillingSection,
  },
  {
    path: 'delete',
    label: 'Delete team',
    description: 'Delete this team and everything it owns.',
    icon: Trash2,
    Component: TeamDeleteSection,
  },
]

/** The page /employees/teams/:teamId itself opens. */
export const DEFAULT_TEAM_CONFIG_SECTION: TeamConfigSection = SIDEBAR_SECTION

/** The one sub-nav heading. */
export const TEAM_CONFIG_GROUP_LABEL = 'Team'
